import React, {
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Customized,
  Line,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  usePlotArea,
  XAxis,
  YAxis,
} from 'recharts'
import styled from 'styled-components'

import { ChartDatapointHoverAnnotation } from 'features/charts-v2/components/ChartDatapointHoverAnnotation'
import {
  SENTIMENT_THRESHOLDS,
  SENTIMENT_ZONE_COLORS,
  SentimentColorGroup,
} from 'features/charts-v2/constants'
import { useBreakdownColorMap } from 'features/charts-v2/context/breakdown-color-map-context'
import { chartConfig } from 'features/charts-v2/services/chart-config-service'
import {
  COMPARISON_SERIES_COLOR,
  DEFAULT_SERIES_COLOR,
} from 'features/charts-v2/services/series-color-palette' // bully ignore[shared-imports-features: applies predicate is always_ts_file (tools/bully/policy/src/rules/rules/shared_imports_features/mod.rs:19), not scoped to configured shared_layer_paths, so it fires on every intra-feature import in this file. follow-up: .agents/follow-ups/bully-typescript-false-positive-rules.md]
import {
  ChartProps,
  ReferenceBounds,
  TransformedChartData,
  TransformedDataPoint,
} from 'features/charts-v2/types/chart-strategy'
import { computeNiceLineDomain } from 'features/charts-v2/utils/axis-calculations'
import {
  calculateAvailableXAxisWidth,
  calculateXAxisHeightFromLabels,
  computeXAxisLabelInterval,
  formatMetricAxisNumber,
  OVERFLOW_CONFIG,
} from 'features/charts-v2/utils/axis-tick-utils'
import { CustomXAxisTick } from 'features/charts-v2/utils/AxisTickUtils'
import { buildBreakdownColorMap } from 'features/charts-v2/utils/breakdown-color-map'
import {
  resolvePointLabelDisplay,
  shouldRenderPointLabels,
} from 'features/charts-v2/utils/datapoint-label-density'
import {
  formatMetricValue,
  getMetricLabel,
  getMetricsLabel,
  getMetricUnitLabel,
} from 'features/charts-v2/utils/metric-format-utils'
import {
  pointLabelClearancePx,
  selectCulledLabelIndexes,
} from 'features/charts-v2/utils/point-label-culling'
import { addTrendlineSeries } from 'features/charts-v2/utils/trendline'

const LINE_CHART_MARGIN = { top: 20, right: 30, left: 20, bottom: 20 } as const

/** Default label placement: 10px above the datapoint. */
const DEFAULT_LABEL_ABOVE_DY = -10

const PRIMARY_LINE_COLOR = DEFAULT_SERIES_COLOR
const COMPARISON_LINE_COLOR = COMPARISON_SERIES_COLOR

/**
 * Styled container that handles hover effects with CSS to avoid React re-renders
 */
const ChartContainer = styled.div`
  width: 100%;
  height: 100%;
`

/**
 * Format number for display in labels
 * - Whole numbers: no decimals with comma separators (e.g., 5, 2,176)
 * - Decimals: max 2 decimal places with comma separators (e.g., 4.91, 1,234.56)
 */
const formatLabelValue = (value: number): string => {
  if (Number.isInteger(value)) {
    return value.toLocaleString()
  }
  return parseFloat(value.toFixed(2)).toLocaleString()
}

/**
 * Custom label component for line data points
 * Displays the value above each point on the line
 */
export const CustomLineLabel = React.memo<{
  x?: number
  y?: number
  value?: number
  index?: number
  labelInterval?: number
  fontSizePx?: number
  color?: string
  opacity?: number
  /** Vertical offset from the data point's y coordinate. Negative = above, positive = below.
   *  Defaults to -10 (label sits above the line) which is the existing behaviour for the main
   *  rating line and all other call sites. Use a positive value to push a label below the line
   *  (e.g. the lower reference-bound edge). */
  dy?: number
  /** Override the default formatLabelValue formatter. Used e.g. for reference-bound labels
   *  on count metrics to round to the nearest whole number. */
  formatValue?: (v: number) => string
  /** Mark the label hover-revealable: applyLineHoverEmphasis shows the
   *  hovered series' labels imperatively (line charts never re-render on
   *  hover). Paired with opacity={0} the label is hidden until hovered;
   *  paired with opacity={1} it renders statically but can be occlusion-
   *  culled (PointLabelCullingLayer) and still reveals while hovered. */
  hoverReveal?: boolean
}>(
  ({
    x = 0,
    y = 0,
    value,
    index,
    labelInterval,
    fontSizePx = 13,
    color = PRIMARY_LINE_COLOR,
    opacity = 1,
    dy = DEFAULT_LABEL_ABOVE_DY,
    formatValue,
    hoverReveal = false,
  }) => {
    if (value == null || value === 0) return null

    // Only render labels at the same indices as the x-axis ticks
    if (
      labelInterval != null &&
      index != null &&
      index % (labelInterval + 1) !== 0
    ) {
      return null
    }

    return (
      <text
        x={x}
        y={y + dy}
        fill={color}
        fontSize={fontSizePx}
        textAnchor="middle"
        opacity={opacity}
        data-hover-reveal={hoverReveal ? '' : undefined}
        style={{
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          // Labels must never intercept hover: an SVG <text> is hit-testable
          // even at opacity 0, and a label floating above a neighbouring
          // series' dot would swallow its mouseenter.
          pointerEvents: 'none',
        }}
      >
        {(formatValue ?? formatLabelValue)(value)}
      </text>
    )
  },
)

CustomLineLabel.displayName = 'CustomLineLabel'

/**
 * Invisible chart layer that occlusion-culls the static value labels in the
 * 'culled' display mode: measures the rendered <text> positions (exact pixel
 * space — no plot-height guessing) and hides all but the topmost label of
 * each per-bucket collision cluster. The per-series label color carries
 * attribution, and hovering a series reveals its culled labels (see
 * setHoverLabelsRevealed). usePlotArea re-fires the pass whenever recharts
 * lays the chart out again (e.g. container resize).
 */
const PointLabelCullingLayer: React.FC<{
  fontSizePx: number
  /** Any value that changes when the chart data changes — re-runs the pass. */
  layoutKey: unknown
}> = ({ fontSizePx, layoutKey }) => {
  const anchorRef = useRef<SVGGElement>(null)
  const plotArea = usePlotArea()

  useLayoutEffect(() => {
    const svg = anchorRef.current?.ownerSVGElement
    if (!svg || !plotArea) return
    const labels = Array.from(
      svg.querySelectorAll<SVGTextElement>('text[data-hover-reveal]'),
    ).filter((label) => label.getAttribute('opacity') !== '0')
    // Reset the previous pass so data/layout changes re-evaluate from
    // scratch — except labels the hover emphasis is actively revealing
    // (data-hover-revealed): surfaces that re-render mid-hover re-run this
    // pass while the hover holds, and hiding a revealed label would undo the
    // reveal with nothing to re-apply it. Such labels still get data-culled
    // so un-hovering re-hides them.
    labels.forEach((label) => {
      label.removeAttribute('data-culled')
      if (!label.hasAttribute('data-hover-revealed')) {
        label.style.visibility = ''
      }
    })
    const culled = selectCulledLabelIndexes(
      labels.map((label) => ({
        x: Number(label.getAttribute('x')),
        y: Number(label.getAttribute('y')),
      })),
      pointLabelClearancePx(fontSizePx),
    )
    culled.forEach((labelIndex) => {
      const label = labels[labelIndex]!
      label.setAttribute('data-culled', '')
      if (!label.hasAttribute('data-hover-revealed')) {
        label.style.visibility = 'hidden'
      }
    })
  }, [plotArea, fontSizePx, layoutKey])

  return <g ref={anchorRef} />
}

/**
 * Custom tooltip that only shows the hovered datapoint
 */
interface CustomTooltipProps {
  active?: boolean
  payload?: Array<{
    name: string
    value: number
    dataKey: string
    color: string
  }>
  label?: string
  hoveredPoint: string | null
  fontSizePx: number
  coordinate?: { x: number; y: number }
}

export interface RechartLabelProps {
  x?: number
  y?: number
  value?: number
  index?: number
}

export const CustomSinglePointTooltip: React.FC<CustomTooltipProps> = ({
  active,
  payload,
  label,
  hoveredPoint,
  fontSizePx: _fontSizePx,
  coordinate,
}) => {
  if (!active || !payload || !hoveredPoint || !coordinate) {
    return null
  }

  const [hoveredCategory, hoveredTime] = hoveredPoint.split('|')

  if (label !== hoveredTime) {
    return null
  }

  const filteredPayload = payload.filter(
    (item) => item.dataKey === hoveredCategory || hoveredCategory === 'single',
  )

  if (filteredPayload.length === 0) {
    return null
  }

  const rows = filteredPayload.map((item) => ({
    label: item.name,
    value: item.value?.toLocaleString() ?? 'No data',
    percentage: null,
    color: item.color,
  }))

  return (
    <ChartDatapointHoverAnnotation
      title={label ?? ''}
      rows={rows}
      footer={null}
      focusedField={null}
    />
  )
}

export interface LineChartMouseEventData {
  activeLabel?: string
}

export interface LineClickEventData {
  payload?: TransformedDataPoint
}

export interface LineDotProps {
  cx: number
  cy: number
  payload?: TransformedDataPoint
  key?: React.Key | null
  index?: number
}

export interface LineChartRendererProps {
  transformedData: TransformedChartData
  data: ChartProps['data']
  /** Unfiltered dataset for stable label→color mapping; see ChartProps.colorReferenceData. */
  colorReferenceData?: ChartProps['colorReferenceData']
  selectedMetrics: ChartProps['selectedMetrics']
  selectedBreakdowns?: ChartProps['selectedBreakdowns']
  onSegmentClick?: ChartProps['onSegmentClick']
  onDataPointHover?: ChartProps['onDataPointHover']
  fontSizePx?: number
  showXAxisLabel?: boolean
  showYAxisLabel?: boolean
  showDatapointLabels?: boolean
  datapointLabelsMode?: ChartProps['datapointLabelsMode']
  xAxisOverflow?: ChartProps['xAxisOverflow']
  yAxisDomain?: ChartProps['yAxisDomain']
  showSentimentZones?: boolean
  showTrendline?: boolean
  referenceBounds?: ReferenceBounds
  disableAnimation?: boolean
}

type LineTooltipData = {
  x: number
  y: number
  category: string
  value: number
  secondaryValue?: number
  label: string
  color: string
  metricType?: 'primary' | 'secondary'
}

export interface LineTooltipBridge {
  set: (data: LineTooltipData | null) => void
}

export const LineChartTooltipOverlay = React.forwardRef<
  LineTooltipBridge,
  {
    fontSizePx: number
    selectedMetrics: ChartProps['selectedMetrics']
    isDualAxis: boolean
    containerRef: React.RefObject<HTMLDivElement | null>
  }
>(
  (
    { fontSizePx: _fontSizePx, selectedMetrics, isDualAxis, containerRef },
    ref,
  ) => {
    const [tooltipData, setTooltipData] = useState<LineTooltipData | null>(null)
    const tooltipDomRef = React.useRef<HTMLDivElement>(null)

    useImperativeHandle(ref, () => ({ set: setTooltipData }), [])

    // The tooltip is opened/closed by mouseenter/mouseleave on the dot
    // <circle>s, and a single lost mouseleave strands it open forever — the
    // page scrolling under a stationary cursor, or a re-render remounting
    // the dot mid-hover (Ask Yogi's conversation surface does both), never
    // delivers the leave event. While open, also dismiss on any scroll (the
    // fixed-position anchor is stale the moment anything scrolls) and on the
    // pointer leaving the chart container, which outlives dot remounts.
    const isOpen = tooltipData != null
    React.useEffect(() => {
      if (!isOpen) return
      const dismiss = (): void => setTooltipData(null)
      window.addEventListener('scroll', dismiss, {
        capture: true,
        passive: true,
      })
      const container = containerRef.current
      container?.addEventListener('mouseleave', dismiss)
      return () => {
        window.removeEventListener('scroll', dismiss, { capture: true })
        container?.removeEventListener('mouseleave', dismiss)
      }
    }, [isOpen, containerRef])

    if (!tooltipData) return null

    // Dot geometry: r=6 + strokeWidth=2 → visible edge is ~7px from center
    const DOT_RADIUS = 7
    const GAP = 4

    // ttWidth used only for horizontal clamp; a stale value shifts the box slightly
    // but never causes the gap error that a stale height caused for the vertical axis.
    const ttWidth = tooltipDomRef.current?.offsetWidth ?? 220
    // estHeight used only for the above/below flip threshold; a wrong estimate
    // only shifts the threshold, it never moves the rendered box edge.
    const estHeight = tooltipDomRef.current?.offsetHeight ?? 130

    const containerRect = containerRef.current?.getBoundingClientRect()
    const leftBound = containerRect?.left ?? 0
    const rightBound = containerRect?.right ?? window.innerWidth
    const topBound = containerRect?.top ?? 0

    // Flip below when there isn't room above (stale estHeight only affects threshold)
    const placeAbove = tooltipData.y - DOT_RADIUS - GAP - estHeight >= topBound

    // Clamp the center point so the box body stays within the chart
    const tooltipLeft = Math.max(
      leftBound + ttWidth / 2,
      Math.min(tooltipData.x, rightBound - ttWidth / 2),
    )
    // Anchor the nearest box edge exactly GAP px from the dot edge.
    // translateY(-100%) pins the bottom; translateY(0) pins the top.
    const tooltipTop = placeAbove
      ? tooltipData.y - DOT_RADIUS - GAP
      : tooltipData.y + DOT_RADIUS + GAP
    const tooltipTransform = `translate(-50%, ${placeAbove ? '-100%' : '0'})`

    const primaryLabel = getMetricsLabel(selectedMetrics)
    const secondaryLabel = isDualAxis
      ? getMetricsLabel(selectedMetrics?.slice(1))
      : undefined

    const rowLabel =
      tooltipData.category !== 'single'
        ? tooltipData.category
        : tooltipData.metricType === 'secondary'
          ? (secondaryLabel ?? 'Secondary')
          : primaryLabel

    const rowMetric =
      tooltipData.metricType === 'secondary'
        ? selectedMetrics?.[1]
        : selectedMetrics?.[0]

    const rows = [
      {
        label: rowLabel,
        value:
          tooltipData.value != null && rowMetric
            ? `${formatMetricValue(tooltipData.value, rowMetric)} ${getMetricUnitLabel(rowMetric)}`
            : (tooltipData.value?.toLocaleString() ?? 'No data'),
        percentage: null,
        color: tooltipData.color,
      },
    ]

    return (
      <div
        ref={tooltipDomRef}
        style={{
          position: 'fixed',
          left: tooltipLeft,
          top: tooltipTop,
          transform: tooltipTransform,
          pointerEvents: 'none',
          zIndex: 9999,
        }}
      >
        <ChartDatapointHoverAnnotation
          title={tooltipData.label}
          rows={rows}
          footer={null}
          focusedField={null}
        />
      </div>
    )
  },
)

LineChartTooltipOverlay.displayName = 'LineChartTooltipOverlay'

/**
 * LineChartRenderer - Functional component that handles chart rendering with hooks
 */
const REF_DEFAULT_COLOR = '#22c55e'
const REF_DEFAULT_FILL = 'rgba(34, 197, 94, 0.15)'

const LineChartRendererInner: React.FC<LineChartRendererProps> = ({
  transformedData,
  data,
  colorReferenceData,
  selectedMetrics,
  selectedBreakdowns,
  onSegmentClick,
  onDataPointHover,
  fontSizePx = 13,
  showXAxisLabel = true,
  showYAxisLabel = true,
  showDatapointLabels: showDatapointLabelsProp = true,
  datapointLabelsMode,
  xAxisOverflow = 'expand',
  yAxisDomain,
  showSentimentZones = true,
  showTrendline = false,
  referenceBounds,
  disableAnimation = false,
}) => {
  const tooltipRef = useRef<LineTooltipBridge | null>(null)
  const chartContainerRef = useRef<HTMLDivElement>(null)
  // Must be called before any early return (rules-of-hooks).
  const contextColorMap = useBreakdownColorMap()

  const breakdowns = selectedBreakdowns || []
  const breakdownCategories = useMemo(
    () => transformedData.metadata?.breakdownCategories || [],
    [transformedData.metadata],
  )
  const hasMultipleBreakdowns =
    breakdowns.length >= 1 && breakdownCategories.length > 0
  const isDualAxis = selectedMetrics.length > 1

  // Resolve the datapoint-label mode against the real series count (only
  // known here, after the transform). 'on' multi-series labels only where
  // there's room: labels render everywhere and the occlusion pass hides
  // colliding ones (PointLabelCullingLayer). Callers that only set the
  // legacy boolean get the same resolution. Series count: one line per
  // breakdown category, else one per metric in dual-axis mode (2 lines),
  // else a single line.
  const seriesCount = hasMultipleBreakdowns
    ? breakdownCategories.length
    : isDualAxis
      ? selectedMetrics.length
      : 1
  const labelDisplay = resolvePointLabelDisplay(
    datapointLabelsMode ?? (showDatapointLabelsProp ? 'on' : 'off'),
    seriesCount,
  )
  // "Every point labeled, no culling" — only single-series charts resolve
  // here; the reference-bound and single-series label sites key off this.
  const showDatapointLabels = labelDisplay === 'all'
  const renderPointLabels = shouldRenderPointLabels(labelDisplay)

  // Compute trendline data keys for primary series (single or multi-breakdown)
  const chartRowsForRender = useMemo(() => {
    const keys: string[] = showTrendline
      ? hasMultipleBreakdowns
        ? breakdownCategories
        : isDualAxis
          ? ['metric1', 'metric2']
          : ['count']
      : []
    let rows: TransformedDataPoint[] =
      keys.length > 0
        ? addTrendlineSeries(transformedData.chartData, keys)
        : transformedData.chartData

    // Merge per-bucket reference bounds into each row so recharts can render them
    // as overlay lines / shaded band. Fields used:
    //   __refLower — lower-bound value (line when both bounds present, or single-line fallback)
    //   __refUpper — upper-bound value (line when both bounds present, or single-line fallback)
    //
    // Band mode uses a range <Area> whose dataKey is a function that returns [__refLower, __refUpper]
    // per row. recharts' computeArea takes the isRange path for array values (Area.js:689-693),
    // tolerating null gracefully as a break point — avoiding the stacked-Area crash that occurs
    // when stackedData[i] is undefined because a null value breaks the d3-stack alignment.
    if (referenceBounds) {
      const hasLower = referenceBounds.lower != null
      const hasUpper = referenceBounds.upper != null
      rows = rows.map((row, i) => {
        const lower = hasLower ? (referenceBounds.lower![i] ?? null) : null
        const upper = hasUpper ? (referenceBounds.upper![i] ?? null) : null
        return {
          ...row,
          ...(hasLower ? { __refLower: lower } : {}),
          ...(hasUpper ? { __refUpper: upper } : {}),
        }
      })
    }

    return rows
  }, [
    transformedData.chartData,
    showTrendline,
    hasMultipleBreakdowns,
    breakdownCategories,
    isDualAxis,
    referenceBounds,
  ])

  if (!transformedData.chartData || transformedData.chartData.length === 0) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div style={{ fontSize: '18px', color: '#666' }}>
          No data available. Try adding some filters.
        </div>
      </div>
    )
  }

  const colorMap =
    contextColorMap.size > 0
      ? contextColorMap
      : buildBreakdownColorMap(
          selectedBreakdowns?.[0] ?? null,
          colorReferenceData ?? data,
        )
  const getCategoryColor = (category: string, index: number): string =>
    colorMap.get(category) ?? chartConfig.getColor('primary', index)

  const estimatedChartWidth = 1200
  const maxWidthPerLabel =
    xAxisOverflow === 'wrap'
      ? calculateAvailableXAxisWidth(
          estimatedChartWidth,
          transformedData.chartData.length,
          { left: 40, right: 30 },
        )
      : 100

  const tickRenderer = (props: {
    x: number
    y: number
    payload: { value: string }
    [k: string]: unknown
  }): JSX.Element => (
    <CustomXAxisTick
      {...props}
      fontSizePx={fontSizePx}
      overflowMode={xAxisOverflow}
      maxWidth={maxWidthPerLabel}
    />
  )

  const labels = transformedData.chartData.map((d) => String(d['name'] ?? ''))

  // Interval shared by the XAxis ticks and datapoint labels — keeps them in
  // sync. Width-aware so weekly (and other dense) time buckets show every label
  // when they fit, rather than every other (PROD-4514).
  const xAxisLabelInterval = computeXAxisLabelInterval(
    transformedData.chartData.length,
    estimatedChartWidth,
  )

  let xAxisHeight: number
  let bottomMargin: number

  if (xAxisOverflow === 'expand') {
    xAxisHeight = calculateXAxisHeightFromLabels(labels, fontSizePx)
    bottomMargin = 20
  } else {
    const overflowConfig = OVERFLOW_CONFIG[xAxisOverflow]
    xAxisHeight = overflowConfig.height
    bottomMargin = overflowConfig.margin
  }

  // Check if sentiment is the metric for background zones
  const isSentimentMetric =
    selectedMetrics.length === 1 && selectedMetrics[0]! === 'sentiment'

  // Compute domain and ticks from transformed data for sentiment/rating metrics
  // This overrides the passed yAxisDomain to ensure data-driven axis sizing
  let computedYAxisDomain: [number, number] | undefined = undefined
  let yAxisTicks: number[] | undefined = undefined
  let computedLeftAxisDomain: [number, number] | undefined = undefined
  let leftAxisTicks: number[] | undefined = undefined
  let computedRightAxisDomain: [number, number] | undefined = undefined
  let rightAxisTicks: number[] | undefined = undefined

  if (!isDualAxis) {
    const metricType = selectedMetrics[0]
    if (metricType === 'sentiment' || metricType === 'rating') {
      // Extract all numeric Y-values from the transformed chart data
      const yValues = transformedData.chartData.flatMap(
        (d: Record<string, unknown>) => {
          // For multi-line charts, extract values from all breakdown categories
          if (hasMultipleBreakdowns && breakdownCategories.length > 0) {
            return breakdownCategories
              .map((cat) => d[cat])
              .filter((v): v is number => typeof v === 'number')
          }
          // For single-line charts, use count/value/metric1
          const value = d['count'] ?? d['value'] ?? d['metric1']
          return typeof value === 'number' ? [value] : []
        },
      )

      // Fold reference-bound values into the domain so a flat threshold like y=1
      // is never clipped off-axis.
      const refValues: number[] = []
      if (referenceBounds) {
        referenceBounds.lower?.forEach((v) => v != null && refValues.push(v))
        referenceBounds.upper?.forEach((v) => v != null && refValues.push(v))
      }

      // Compute data-driven domain and ticks
      const allValues = [...yValues, ...refValues]
      if (allValues.length > 0) {
        const axisLabelMin = allValues.reduce(
          (a, b) => Math.min(a, b),
          Infinity,
        )
        const axisLabelMax = allValues.reduce(
          (a, b) => Math.max(a, b),
          -Infinity,
        )
        const niceDomain = computeNiceLineDomain(
          [axisLabelMin, axisLabelMax],
          metricType,
        )
        if (niceDomain) {
          computedYAxisDomain = niceDomain.domain
          yAxisTicks = niceDomain.ticks
        }
      }
    }
  } else {
    // Dual-axis mode: compute domain and ticks for each axis independently
    const leftMetricType = selectedMetrics[0]
    const rightMetricType = selectedMetrics[1]

    // Left axis (primary metric)
    if (leftMetricType === 'sentiment' || leftMetricType === 'rating') {
      const leftYValues = transformedData.chartData.flatMap(
        (d: Record<string, unknown>) => {
          if (hasMultipleBreakdowns && breakdownCategories.length > 0) {
            return breakdownCategories
              .map((cat) => d[cat])
              .filter((v): v is number => typeof v === 'number')
          }
          const value = d['metric1']
          return typeof value === 'number' ? [value] : []
        },
      )

      if (leftYValues.length > 0) {
        const dataMin = Math.min(...leftYValues)
        const dataMax = Math.max(...leftYValues)
        const niceDomain = computeNiceLineDomain(
          [dataMin, dataMax],
          leftMetricType,
        )
        if (niceDomain) {
          computedLeftAxisDomain = niceDomain.domain
          leftAxisTicks = niceDomain.ticks
        }
      }
    }

    // Right axis (secondary metric)
    if (rightMetricType === 'sentiment' || rightMetricType === 'rating') {
      const rightYValues = transformedData.chartData.flatMap(
        (d: Record<string, unknown>) => {
          if (hasMultipleBreakdowns && breakdownCategories.length > 0) {
            return breakdownCategories
              .map((cat) => d[`${cat}__m2`])
              .filter((v): v is number => typeof v === 'number')
          }
          const value = d['metric2']
          return typeof value === 'number' ? [value] : []
        },
      )

      if (rightYValues.length > 0) {
        const dataMin = Math.min(...rightYValues)
        const dataMax = Math.max(...rightYValues)
        const niceDomain = computeNiceLineDomain(
          [dataMin, dataMax],
          rightMetricType,
        )
        if (niceDomain) {
          computedRightAxisDomain = niceDomain.domain
          rightAxisTicks = niceDomain.ticks
        }
      }
    }
  }

  // Use computed domain if available, otherwise fall back to passed domain
  const effectiveYAxisDomain = computedYAxisDomain ?? yAxisDomain
  const clipsAtCountFloor =
    (selectedMetrics[0] === 'record_count' ||
      selectedMetrics[0] === 'sentence_count') &&
    effectiveYAxisDomain?.[0] === 0

  // Calculate sentiment background zones based on actual data domain
  const sentimentZones: Array<{ y1: number; y2: number; fill: string }> = []
  if (isSentimentMetric && showSentimentZones) {
    // Use the effective Y-axis domain for sentiment zones
    const domainMin = effectiveYAxisDomain?.[0] ?? -1
    const domainMax = effectiveYAxisDomain?.[1] ?? 1

    // Calculate visible zones, clipped to domain
    // Red zone: values < -0.3
    if (domainMin < SENTIMENT_THRESHOLDS.NEGATIVE) {
      const y1 = Math.max(domainMin, -1)
      const y2 = SENTIMENT_THRESHOLDS.NEGATIVE
      if (y2 > y1) {
        sentimentZones.push({
          y1,
          y2,
          fill: SENTIMENT_ZONE_COLORS[SentimentColorGroup.NEGATIVE],
        })
      }
    }

    // Yellow zone: values -0.3 to 0.3
    if (
      domainMin < SENTIMENT_THRESHOLDS.POSITIVE &&
      domainMax > SENTIMENT_THRESHOLDS.NEGATIVE
    ) {
      const y1 = Math.max(domainMin, SENTIMENT_THRESHOLDS.NEGATIVE)
      const y2 = Math.min(domainMax, SENTIMENT_THRESHOLDS.POSITIVE)
      if (y2 > y1) {
        sentimentZones.push({
          y1,
          y2,
          fill: SENTIMENT_ZONE_COLORS[SentimentColorGroup.NEUTRAL],
        })
      }
    }

    // Green zone: values > 0.3
    if (domainMax > SENTIMENT_THRESHOLDS.POSITIVE) {
      const y1 = Math.max(domainMin, SENTIMENT_THRESHOLDS.POSITIVE)
      const y2 = Math.min(domainMax, 1)
      if (y2 > y1) {
        sentimentZones.push({
          y1,
          y2,
          fill: SENTIMENT_ZONE_COLORS[SentimentColorGroup.POSITIVE],
        })
      }
    }
  }

  // Formatter for reference-bound labels: round count metrics to whole numbers
  // (e.g. 59,230.69 → 59,231) while keeping decimals for sentiment/rating.
  const primaryMetric = selectedMetrics[0]
  const boundLabelFormatter = (v: number): string => {
    if (primaryMetric === 'sentiment' || primaryMetric === 'rating') {
      return parseFloat(v.toFixed(2)).toLocaleString()
    }
    return Math.round(v).toLocaleString()
  }

  return (
    <ChartContainer ref={chartContainerRef}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          key={`chart-${yAxisDomain?.[1] ?? 'auto'}`}
          data={chartRowsForRender}
          margin={{ ...LINE_CHART_MARGIN, bottom: bottomMargin }}
        >
          <CartesianGrid strokeDasharray="3 3" />

          {/* Sentiment background zones */}
          {sentimentZones.map((zone, index) => (
            <ReferenceArea
              key={`sentiment-zone-${index}`}
              y1={zone.y1}
              y2={zone.y2}
              fill={zone.fill}
              fillOpacity={1}
            />
          ))}

          <XAxis
            dataKey="name"
            height={xAxisHeight}
            interval={xAxisLabelInterval}
            tick={showXAxisLabel ? tickRenderer : false}
          />

          {/* Dual-axis: left Y-axis for first metric, right Y-axis for second metric */}
          {isDualAxis ? (
            <>
              <YAxis
                orientation="left"
                tick={{ fontSize: fontSizePx }}
                tickFormatter={(value) =>
                  formatMetricAxisNumber(value, selectedMetrics[0])
                }
                domain={computedLeftAxisDomain || undefined}
                ticks={leftAxisTicks}
                label={
                  showYAxisLabel
                    ? {
                        value: getMetricsLabel([selectedMetrics[0]!]),
                        angle: -90,
                        position: 'insideLeft',
                        style: {
                          textAnchor: 'middle',
                          fill: '#666',
                          fontSize: fontSizePx,
                        },
                      }
                    : undefined
                }
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fontSize: fontSizePx }}
                tickFormatter={(value) =>
                  formatMetricAxisNumber(value, selectedMetrics[1])
                }
                domain={computedRightAxisDomain || undefined}
                ticks={rightAxisTicks}
                label={
                  showYAxisLabel
                    ? {
                        value: getMetricsLabel([selectedMetrics[1]!]),
                        angle: 90,
                        position: 'insideRight',
                        style: {
                          textAnchor: 'middle',
                          fill: '#666',
                          fontSize: fontSizePx,
                        },
                      }
                    : undefined
                }
              />
            </>
          ) : (
            <YAxis
              allowDataOverflow={clipsAtCountFloor}
              tick={{ fontSize: fontSizePx }}
              tickFormatter={(value) =>
                formatMetricAxisNumber(value, selectedMetrics[0])
              }
              domain={effectiveYAxisDomain || undefined}
              ticks={yAxisTicks}
              label={
                showYAxisLabel
                  ? {
                      value: getMetricsLabel([selectedMetrics[0]!]),
                      angle: -90,
                      position: 'insideLeft',
                      style: {
                        textAnchor: 'middle',
                        fill: '#666',
                        fontSize: fontSizePx,
                      },
                    }
                  : undefined
              }
            />
          )}
          {hasMultipleBreakdowns ? null : (
            <Tooltip
              isAnimationActive={false}
              content={({ active, payload, label }) => {
                if (!active || !payload || payload.length === 0) return null
                const seenMetrics = new Set<string>()
                const rows = payload
                  // Drop reference-bound series (band range-Area has a function dataKey;
                  // bound edge Lines are keyed with the '__ref' prefix). Keeping them in the
                  // tooltip payload causes the band to claim selectedMetrics[0] first, render
                  // the array value as "No data", and dedup the real rating line out entirely.
                  .filter(
                    (item) =>
                      typeof item.dataKey !== 'function' &&
                      !String(item.dataKey ?? '').startsWith('__ref'),
                  )
                  .map((item) => {
                    const metric =
                      String(item.dataKey ?? '') === 'metric2'
                        ? selectedMetrics[1]
                        : selectedMetrics[0]
                    if (!metric || seenMetrics.has(metric)) return null
                    seenMetrics.add(metric)
                    return {
                      label: getMetricLabel(metric),
                      value:
                        typeof item.value === 'number'
                          ? formatMetricValue(item.value, metric)
                          : 'No data',
                      percentage: null as string | null,
                      color: item.color ?? null,
                    }
                  })
                  .filter((row): row is NonNullable<typeof row> => row !== null)
                if (rows.length === 0) return null
                return (
                  <ChartDatapointHoverAnnotation
                    title={String(label ?? '')}
                    rows={rows}
                    footer={null}
                    focusedField={null}
                  />
                )
              }}
            />
          )}

          {/* Reference bounds: overlay lines / shaded band for alert thresholds.
              Rendered before the main data series so the primary line sits on top.
              Only active for the non-breakdown, non-dual-axis (single rating line) case
              which is the only shape alert preview charts use. */}
          {referenceBounds && !hasMultipleBreakdowns && !isDualAxis ? (
            <>
              {referenceBounds.lower != null &&
              referenceBounds.upper != null ? (
                // Band mode: a range Area fills the region between the two bounds; two Lines
                // draw the visible lower and upper edges.
                //
                // We use recharts' range-Area primitive (__refBand dataKey returns a [lo, hi]
                // tuple) rather than two stacked Areas. The stacked approach crashes when any
                // row has a null stacked value: computeArea reads stackedData[i] expecting a
                // [base, top] tuple but gets undefined, throwing "Cannot read ... '1'".
                // The range path tolerates null gracefully (treated as a gap).
                <>
                  <Area
                    type="monotone"
                    dataKey={(row: TransformedDataPoint) => {
                      // Return a [lower, upper] tuple so recharts takes the isRange path in
                      // computeArea (Area.js:689-693). isRange tolerates null as a break point,
                      // avoiding the stacked-Area crash on rows where bounds are undefined.
                      const lo = row['__refLower']
                      const hi = row['__refUpper']
                      return lo != null && hi != null ? [lo, hi] : null
                    }}
                    stroke="none"
                    fill={referenceBounds.fill ?? REF_DEFAULT_FILL}
                    dot={false}
                    activeDot={false}
                    isAnimationActive={false}
                    connectNulls={false}
                    legendType="none"
                    name={referenceBounds.label ?? 'Bounds Band'}
                  />
                  <Line
                    type="monotone"
                    dataKey="__refLower"
                    stroke={referenceBounds.color ?? REF_DEFAULT_COLOR}
                    strokeWidth={1.5}
                    dot={false}
                    activeDot={false}
                    isAnimationActive={
                      !showDatapointLabels && !disableAnimation
                    }
                    connectNulls={false}
                    legendType="none"
                    name={
                      referenceBounds.label
                        ? `${referenceBounds.label} (lower)`
                        : 'Lower Bound'
                    }
                    label={
                      showDatapointLabels
                        ? (p: RechartLabelProps) => (
                            <CustomLineLabel
                              {...p}
                              fontSizePx={fontSizePx}
                              labelInterval={xAxisLabelInterval}
                              color={referenceBounds.color ?? REF_DEFAULT_COLOR}
                              dy={fontSizePx + 6}
                              formatValue={boundLabelFormatter}
                            />
                          )
                        : false
                    }
                  />
                  <Line
                    type="monotone"
                    dataKey="__refUpper"
                    stroke={referenceBounds.color ?? REF_DEFAULT_COLOR}
                    strokeWidth={1.5}
                    dot={false}
                    activeDot={false}
                    isAnimationActive={
                      !showDatapointLabels && !disableAnimation
                    }
                    connectNulls={false}
                    legendType="none"
                    name={referenceBounds.label ?? 'Upper Bound'}
                    label={
                      showDatapointLabels
                        ? (p: RechartLabelProps) => (
                            <CustomLineLabel
                              {...p}
                              fontSizePx={fontSizePx}
                              labelInterval={xAxisLabelInterval}
                              color={referenceBounds.color ?? REF_DEFAULT_COLOR}
                              dy={-10}
                              formatValue={boundLabelFormatter}
                            />
                          )
                        : false
                    }
                  />
                </>
              ) : (
                // Single-line mode: one bound line, no fill.
                <Line
                  type="monotone"
                  dataKey={
                    referenceBounds.lower != null ? '__refLower' : '__refUpper'
                  }
                  stroke={referenceBounds.color ?? REF_DEFAULT_COLOR}
                  strokeWidth={1.5}
                  dot={false}
                  activeDot={false}
                  isAnimationActive={!showDatapointLabels && !disableAnimation}
                  connectNulls={false}
                  legendType="none"
                  name={referenceBounds.label ?? 'Bound'}
                  label={
                    showDatapointLabels
                      ? (p: RechartLabelProps) => (
                          <CustomLineLabel
                            {...p}
                            fontSizePx={fontSizePx}
                            labelInterval={xAxisLabelInterval}
                            color={referenceBounds.color ?? REF_DEFAULT_COLOR}
                            dy={-10}
                            formatValue={boundLabelFormatter}
                          />
                        )
                      : false
                  }
                />
              )}
            </>
          ) : null}

          {/* Render areas first (for secondary metric in dual-axis mode) so they appear behind lines */}
          {hasMultipleBreakdowns && isDualAxis
            ? breakdownCategories.map((category, index) => (
                <Area
                  key={`${category}__m2`}
                  type="monotone"
                  dataKey={`${category}__m2`}
                  fill={getCategoryColor(category, index)}
                  fillOpacity={0.25}
                  stroke={getCategoryColor(category, index)}
                  strokeWidth={1.5}
                  yAxisId="right"
                  isAnimationActive={false}
                  dot={(dotProps: LineDotProps) => (
                    <circle
                      key={dotProps.key ?? `area-dot-${dotProps.index ?? 0}`}
                      cx={dotProps.cx}
                      cy={dotProps.cy}
                      r={0}
                      data-line-name={category}
                    />
                  )}
                />
              ))
            : null}

          {!hasMultipleBreakdowns && isDualAxis ? (
            <Area
              type="monotone"
              dataKey="metric2"
              fill={COMPARISON_LINE_COLOR}
              fillOpacity={0.25}
              stroke={COMPARISON_LINE_COLOR}
              strokeWidth={1.5}
              yAxisId="right"
              isAnimationActive={false}
            />
          ) : null}

          {/* Render secondary metric lines (dashed) for dual-axis mode */}
          {hasMultipleBreakdowns && isDualAxis
            ? breakdownCategories.map((category, index) => (
                <Line
                  key={`${category}__m2-line`}
                  type="monotone"
                  dataKey={`${category}__m2`}
                  stroke={getCategoryColor(category, index)}
                  strokeWidth={1.5}
                  strokeDasharray="4 3"
                  name={`${category} (${getMetricsLabel([selectedMetrics[1]!])})`}
                  connectNulls={false}
                  yAxisId="right"
                  isAnimationActive={false}
                  label={(labelProps: RechartLabelProps) => (
                    <CustomLineLabel
                      {...labelProps}
                      fontSizePx={fontSizePx}
                      color={getCategoryColor(category, index)}
                      labelInterval={xAxisLabelInterval}
                      opacity={labelDisplay === 'hover-only' ? 0 : 1}
                      hoverReveal
                    />
                  )}
                  dot={(dotProps: LineDotProps) => {
                    const value = dotProps.payload?.[`${category}__m2`]
                    if (value == null) {
                      return (
                        <React.Fragment
                          key={dotProps.key ?? `dot-${dotProps.index ?? 0}`}
                        />
                      )
                    }

                    const lineColor = getCategoryColor(category, index)

                    return (
                      <circle
                        key={dotProps.key ?? `dot-${dotProps.index ?? 0}`}
                        cx={dotProps.cx}
                        cy={dotProps.cy}
                        r={6}
                        fill={lineColor}
                        stroke="white"
                        strokeWidth={2}
                        data-line-name={category}
                        style={{ cursor: 'pointer', pointerEvents: 'all' }}
                        onMouseEnter={(
                          event: React.MouseEvent<SVGCircleElement>,
                        ) => {
                          const secVal = dotProps.payload?.[
                            `${category}__m2`
                          ] as number | undefined
                          const secRect =
                            event.currentTarget.getBoundingClientRect()
                          tooltipRef.current?.set({
                            x: secRect.left + secRect.width / 2,
                            y: secRect.top + secRect.height / 2,
                            category,
                            value: secVal ?? 0,
                            label: String(dotProps.payload?.['name'] ?? ''),
                            color: lineColor,
                            metricType: 'secondary',
                          })
                          onDataPointHover?.(category)
                        }}
                        onMouseLeave={() => {
                          tooltipRef.current?.set(null)
                          onDataPointHover?.(null)
                        }}
                        onClick={(event: React.MouseEvent) => {
                          event.stopPropagation()
                          if (onSegmentClick && dotProps.payload) {
                            onSegmentClick(
                              {
                                primaryBreakdown: String(
                                  dotProps.payload['name'] ?? '',
                                ),
                                stackingBreakdown: category,
                                value:
                                  Number(dotProps.payload[`${category}__m2`]) ||
                                  0,
                                payload: dotProps.payload as Record<
                                  string,
                                  string | number | boolean | null
                                >,
                              },
                              event,
                            )
                          }
                        }}
                      />
                    )
                  }}
                  activeDot={false}
                />
              ))
            : null}

          {/* Render lines (primary metric) */}
          {hasMultipleBreakdowns ? (
            breakdownCategories.map((category, index) => (
              <Line
                key={category}
                type="monotone"
                dataKey={category}
                stroke={getCategoryColor(category, index)}
                strokeWidth={2}
                name={category}
                connectNulls={false}
                // Labels need their DOM at first paint — recharts defers
                // label rendering until the intro animation ends, which
                // would starve the culling pass ('all'/'culled') and leave
                // hover-reveal nothing to show during the intro
                // ('hover-only').
                isAnimationActive={false}
                label={(labelProps: RechartLabelProps) => (
                  <CustomLineLabel
                    {...labelProps}
                    fontSizePx={fontSizePx}
                    color={getCategoryColor(category, index)}
                    labelInterval={xAxisLabelInterval}
                    opacity={labelDisplay === 'hover-only' ? 0 : 1}
                    hoverReveal
                  />
                )}
                dot={(dotProps: LineDotProps) => {
                  const value = dotProps.payload?.[category]
                  if (value == null) {
                    return (
                      <React.Fragment
                        key={dotProps.key ?? `dot-${dotProps.index ?? 0}`}
                      />
                    )
                  }

                  const lineColor = getCategoryColor(category, index)

                  return (
                    <circle
                      key={dotProps.key ?? `dot-${dotProps.index ?? 0}`}
                      cx={dotProps.cx}
                      cy={dotProps.cy}
                      r={6}
                      fill={lineColor}
                      stroke="white"
                      strokeWidth={2}
                      data-line-name={category}
                      style={{ cursor: 'pointer', pointerEvents: 'all' }}
                      onMouseEnter={(
                        event: React.MouseEvent<SVGCircleElement>,
                      ) => {
                        const secondaryValue = isDualAxis
                          ? (dotProps.payload?.[`${category}__m2`] as
                              | number
                              | undefined)
                          : undefined
                        const dotRect =
                          event.currentTarget.getBoundingClientRect()
                        tooltipRef.current?.set({
                          x: dotRect.left + dotRect.width / 2,
                          y: dotRect.top + dotRect.height / 2,
                          category,
                          value: Number(dotProps.payload?.[category] ?? 0),
                          secondaryValue,
                          label: String(dotProps.payload?.['name'] ?? ''),
                          color: lineColor,
                          metricType: 'primary',
                        })
                        onDataPointHover?.(category)
                      }}
                      onMouseLeave={() => {
                        tooltipRef.current?.set(null)
                        onDataPointHover?.(null)
                      }}
                      onClick={(event: React.MouseEvent) => {
                        event.stopPropagation()
                        if (onSegmentClick && dotProps.payload) {
                          onSegmentClick(
                            {
                              primaryBreakdown: String(
                                dotProps.payload['name'] ?? '',
                              ),
                              stackingBreakdown: category,
                              value: Number(dotProps.payload[category]) || 0,
                              payload: dotProps.payload as Record<
                                string,
                                string | number | boolean | null
                              >,
                            },
                            event,
                          )
                        }
                      }}
                    />
                  )
                }}
                activeDot={false}
              />
            ))
          ) : (
            <Line
              type="monotone"
              dataKey={isDualAxis ? 'metric1' : 'count'}
              stroke={PRIMARY_LINE_COLOR}
              strokeWidth={2}
              name={getMetricLabel(selectedMetrics[0]!)}
              isAnimationActive={!renderPointLabels && !disableAnimation}
              label={
                renderPointLabels
                  ? (labelProps: RechartLabelProps) => (
                      <CustomLineLabel
                        {...labelProps}
                        fontSizePx={fontSizePx}
                        labelInterval={xAxisLabelInterval}
                        hoverReveal={labelDisplay === 'culled'}
                      />
                    )
                  : false
              }
              dot={(dotProps: LineDotProps) => {
                const valueKey = isDualAxis ? 'metric1' : 'count'
                const value = dotProps.payload?.[valueKey]
                if (value == null) {
                  return (
                    <React.Fragment
                      key={dotProps.key ?? `dot-${dotProps.index ?? 0}`}
                    />
                  )
                }
                const lower = dotProps.payload?.['__refLower']
                const upper = dotProps.payload?.['__refUpper']
                const isAlertTrigger =
                  typeof value === 'number' &&
                  ((typeof lower === 'number' && value < lower) ||
                    (typeof upper === 'number' && value > upper))
                const dotColor = isAlertTrigger ? '#ef4444' : PRIMARY_LINE_COLOR

                return (
                  <circle
                    key={dotProps.key ?? `dot-${dotProps.index ?? 0}`}
                    cx={dotProps.cx}
                    cy={dotProps.cy}
                    r={6}
                    fill={dotColor}
                    stroke="white"
                    strokeWidth={2}
                    style={{ cursor: 'pointer', pointerEvents: 'all' }}
                    onMouseEnter={(
                      event: React.MouseEvent<SVGCircleElement>,
                    ) => {
                      const secondaryValue = isDualAxis
                        ? (dotProps.payload?.['metric2'] as number | undefined)
                        : undefined
                      tooltipRef.current?.set({
                        x: event.clientX,
                        y: event.clientY,
                        category: 'single',
                        value: Number(dotProps.payload?.[valueKey] ?? 0),
                        secondaryValue,
                        label: String(dotProps.payload?.['name'] ?? ''),
                        color: dotColor,
                        metricType: 'primary',
                      })
                    }}
                    onMouseLeave={() => {
                      tooltipRef.current?.set(null)
                    }}
                    onClick={(event: React.MouseEvent) => {
                      event.stopPropagation()
                      if (onSegmentClick && dotProps.payload) {
                        onSegmentClick(
                          {
                            primaryBreakdown: String(
                              dotProps.payload['name'] ?? '',
                            ),
                            value: Number(dotProps.payload[valueKey]) || 0,
                            payload: dotProps.payload as Record<
                              string,
                              string | number | boolean | null
                            >,
                          },
                          event,
                        )
                      }
                    }}
                  />
                )
              }}
              activeDot={false}
            />
          )}

          {/* Trendline: one dashed best-fit line per primary series.
              Use hide={!showTrendline} instead of mount/unmount so recharts
              reconciles the SVG path away immediately on toggle-off — unmounting
              a <Line> leaves a stale drawn curve until the chart remounts. */}
          {hasMultipleBreakdowns
            ? breakdownCategories.map((category, index) => (
                <Line
                  key={`${category}__trendline`}
                  type="linear"
                  dataKey={`${category}__trend`}
                  stroke={getCategoryColor(category, index)}
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={false}
                  activeDot={false}
                  isAnimationActive={false}
                  connectNulls
                  legendType="none"
                  name={`${category} trend`}
                  hide={!showTrendline}
                />
              ))
            : null}
          {!hasMultipleBreakdowns ? (
            <Line
              type="linear"
              dataKey={`${isDualAxis ? 'metric1' : 'count'}__trend`}
              stroke={PRIMARY_LINE_COLOR}
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={false}
              activeDot={false}
              isAnimationActive={false}
              connectNulls
              legendType="none"
              name="trend"
              hide={!showTrendline}
            />
          ) : null}
          {!hasMultipleBreakdowns && isDualAxis ? (
            <Line
              type="linear"
              dataKey="metric2__trend"
              stroke={COMPARISON_LINE_COLOR}
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={false}
              activeDot={false}
              isAnimationActive={false}
              connectNulls
              legendType="none"
              name="trend"
              yAxisId="right"
              hide={!showTrendline}
            />
          ) : null}

          {/* Render secondary metric line (dashed) for single dual-axis mode */}
          {!hasMultipleBreakdowns && isDualAxis ? (
            <Line
              type="monotone"
              dataKey="metric2"
              stroke={COMPARISON_LINE_COLOR}
              strokeWidth={1.5}
              strokeDasharray="4 3"
              name={getMetricsLabel([selectedMetrics[1]!])}
              yAxisId="right"
              isAnimationActive={false}
              label={
                renderPointLabels
                  ? (labelProps: RechartLabelProps) => (
                      <CustomLineLabel
                        {...labelProps}
                        fontSizePx={fontSizePx}
                        color={COMPARISON_LINE_COLOR}
                        labelInterval={xAxisLabelInterval}
                        hoverReveal={labelDisplay === 'culled'}
                      />
                    )
                  : false
              }
              dot={(dotProps: LineDotProps) => {
                const value = dotProps.payload?.['metric2']
                if (value == null) {
                  return (
                    <React.Fragment
                      key={dotProps.key ?? `dot-${dotProps.index ?? 0}`}
                    />
                  )
                }

                const dotColor = COMPARISON_LINE_COLOR

                return (
                  <circle
                    key={dotProps.key ?? `dot-${dotProps.index ?? 0}`}
                    cx={dotProps.cx}
                    cy={dotProps.cy}
                    r={6}
                    fill={dotColor}
                    stroke="white"
                    strokeWidth={2}
                    style={{ cursor: 'pointer', pointerEvents: 'all' }}
                    onMouseEnter={(
                      event: React.MouseEvent<SVGCircleElement>,
                    ) => {
                      const primaryValue = dotProps.payload?.['metric1'] as
                        | number
                        | undefined
                      tooltipRef.current?.set({
                        x: event.clientX,
                        y: event.clientY,
                        category: 'single',
                        value: Number(dotProps.payload?.['metric2'] ?? 0),
                        secondaryValue: primaryValue,
                        label: String(dotProps.payload?.['name'] ?? ''),
                        color: dotColor,
                        metricType: 'secondary',
                      })
                    }}
                    onMouseLeave={() => {
                      tooltipRef.current?.set(null)
                    }}
                    onClick={(event: React.MouseEvent) => {
                      event.stopPropagation()
                      if (onSegmentClick && dotProps.payload) {
                        onSegmentClick(
                          {
                            primaryBreakdown: String(
                              dotProps.payload['name'] ?? '',
                            ),
                            value: Number(dotProps.payload['metric2']) || 0,
                            payload: dotProps.payload as Record<
                              string,
                              string | number | boolean | null
                            >,
                          },
                          event,
                        )
                      }
                    }}
                  />
                )
              }}
              activeDot={false}
            />
          ) : null}

          {labelDisplay === 'culled' ? (
            <Customized
              component={
                <PointLabelCullingLayer
                  fontSizePx={fontSizePx}
                  layoutKey={chartRowsForRender}
                />
              }
            />
          ) : null}
        </ComposedChart>
      </ResponsiveContainer>

      {hasMultipleBreakdowns && (
        <LineChartTooltipOverlay
          ref={tooltipRef}
          containerRef={chartContainerRef}
          fontSizePx={fontSizePx}
          selectedMetrics={selectedMetrics}
          isDualAxis={isDualAxis}
        />
      )}
    </ChartContainer>
  )
}

export const LineChartRenderer = React.memo(LineChartRendererInner)
